<template>
  <BMDrawerSheet
    v-if="isBekkiEnabled"
    :modelValue="bekkiChatStore.isOpen"
    :width="DRAWER_WIDTH"
    :ariaLabel="t('widgets.bekki.title')"
    data-cc="bekki-drawer"
    :data-state="bekkiChatStore.isOpen ? 'open' : 'closed'"
    disableRouteWatcher
    @update:modelValue="onDrawerUpdate"
  >
    <template #header>
      <div class="d-flex align-center ga-2 pa-6">
        <BekkiAvatar />

        <span class="text-h5">{{ t('widgets.bekki.title') }}</span>

        <v-spacer />

        <div class="d-flex align-center ga-1">
          <v-tooltip
            v-if="bekkiChatStore.hasMessages"
            :text="t('widgets.bekki.newConversation')"
            location="bottom"
          >
            <template #activator="{ props }">
              <v-icon-btn
                v-bind="props"
                icon="huge:square-pen"
                variant="text"
                color="secondary"
                size="small"
                :aria-label="t('widgets.bekki.newConversation')"
                data-cc="bekki-new-chat"
                @click="bekkiChatStore.newConversation"
              />
            </template>
          </v-tooltip>

          <v-icon-btn
            icon="$close"
            variant="text"
            color="secondary"
            size="small"
            :aria-label="t('generic.close')"
            data-cc="bekki-close"
            @click="bekkiChatStore.close"
          />
        </div>
      </div>
    </template>

    <BekkiNewChatState
      v-if="bekkiChatStore.isFreshConversation && !bekkiChatStore.hasMessages"
    />
    <BekkiEmptyState v-else-if="!bekkiChatStore.hasMessages" />
    <BekkiMessageList v-else />

    <template #footer>
      <BekkiComposer />
    </template>
  </BMDrawerSheet>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { onKeyStroke } from '@vueuse/core';

import BekkiAvatar from '@/components/bekki/BekkiAvatar.vue';
import BekkiComposer from '@/components/bekki/BekkiComposer.vue';
import BekkiEmptyState from '@/components/bekki/BekkiEmptyState.vue';
import BekkiMessageList from '@/components/bekki/BekkiMessageList.vue';
import BekkiNewChatState from '@/components/bekki/BekkiNewChatState.vue';
import BMDrawerSheet from '@/components/common/utilities/BMDrawerSheet.vue';

import { useBekkiAccess } from '@/composables/useBekkiAccess';
import useBekkiChatStore from '@/stores/bekkiChat';

const { t } = useI18n();

const { isBekkiEnabled } = useBekkiAccess();
const bekkiChatStore = useBekkiChatStore();

/** Figma panel width; BMDrawerSheet's default is 420. */
const DRAWER_WIDTH = 400;

/** Scrim click — the drawer only ever reports `false` from here. */
const onDrawerUpdate = (isOpen: boolean): void => {
  if (!isOpen) bekkiChatStore.close();
};

// v-navigation-drawer has no Esc handling of its own. A dialog or menu open on
// top (VOverlay, not preventDefault-ing its Esc) takes that Esc alone — the
// class is still in the DOM while its window listener handles the same event.
// Snackbars and tooltips are VOverlays too, but outside the overlay stack: they
// never take Esc.
const ESC_TAKING_OVERLAY =
  '.v-overlay--active:not(.v-snackbar):not(.v-tooltip)';

onKeyStroke('Escape', () => {
  if (!bekkiChatStore.isOpen) return;
  if (document.querySelector(ESC_TAKING_OVERLAY)) return;
  bekkiChatStore.close();
});
</script>
